import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Tooltip, IconButton, Fab } from '@material-ui/core';
import { BackButton, localization, hasPermission, getDimensionsOfPagination } from '../../sc-utilities';
import { Add, Menu } from '@material-ui/icons';
import { Delete, Refresh } from '@material-ui/icons';
import MaterialTable from 'material-table';
import { parseURL } from '../../sc-utilities';
import ColliTypeDelete from './ColliTypeDelete';
import * as actions from './store/colliTypeActions';
import * as action from '../../np-core/Auth/store/authActions';
import Toolbar from '../../sc-utilities/Toolbar';
import ColliTypePresetForm from './ColliTypePreset/ColliTypePresetForm';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const ColliTypeList = props => {
    const { classes, translations, permissions, onFetchColliTypeList, onFetchColliTypeListCount, fetching, deleting, saveColumnsFilter, activeCustomerID, onBulkDeleteColliTypeTypes, tableColumns, redirectHandler, onGeneratePreset, warehouseObj, tableRef } = props;
    const [deleteModalState, setDeleteModalState] = useState({ open: false, colliTypeIDs: [], forBulk: false });
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);
    const [presetModalState, setPresetModalState] = useState({ open: false, custom: false, type: "simple", productTypes: [], warehouseObj: warehouseObj });
    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: true,
        selectionProps: rowData => {
            return { id: `checkbox_${rowData.ColliTypeID}` }
        },
        search: false,
        minBodyHeight: window.innerHeight - 205 < 300 ? 300 : window.innerHeight - 205,
        maxBodyHeight: window.innerHeight - 205,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? '#f3f3f3' : '#ffffff' };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const filterQuery = filters => {
        let obj = { ...parseURL(window.location.href) };
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }




    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;
        let count = await onFetchColliTypeListCount(query);
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count ? await onFetchColliTypeList(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }


    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography >{translations['colli_types'] || 'Colli types'}</Typography>
                {hasPermission(permissions, 'colli_type_create') ? <Fab
                    id="create_colli_type"
                    variant="extended"
                    size="small"
                    color="primary"
                    aria-label="Add"
                    style={{ borderRadius: '1px' }}
                    className={classes.fab}
                    onClick={() => redirectHandler(`/colli-type/create`, null)}
                >
                    <Add size="small" />
                    {translations["create_new"] || "Create new"}
                </Fab> : null}
                {hasPermission(permissions, 'colli_type_create') ? <Fab
                    id="create_colli_type_preset"
                    variant="extended"
                    size="small"
                    style={{ borderRadius: '1px' }}
                    color="primary"
                    aria-label="Menu"
                    className={classes.fab}
                    onClick={handleOpenPreset}
                >
                    <Menu size="small" />
                    {translations["create_from_preset"] || "Create from preset"}
                </Fab> : null}
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    }

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, colliTypeIDs: [], forBulk: false });
    }

    const handleCloseDanger = async () => {
        await onBulkDeleteColliTypeTypes({ ColliTypeIDs: deleteModalState.colliTypeIDs, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, colliTypeIDs: [], forBulk: false });
        tableRef.current && tableRef.current.onQueryChange()
    };

    const handleBulkDelete = colliTypeIDs => {
        setDeleteModalState({ ...deleteModalState, open: true, colliTypeIDs: colliTypeIDs, forBulk: true });
    };

    const handleClosePresetSafe = () => {
        setPresetModalState({ open: false, custom: false, type: "simple", productTypes: [], warehouseObj: warehouseObj });
    };

    const handleClosePresetDanger = async () => {
        setPresetModalState({ open: false, custom: false, type: "simple", productTypes: [], warehouseObj: warehouseObj });
        tableRef.current && tableRef.current.onQueryChange()
    };

    const handleOpenPreset = () => {
        setPresetModalState({ ...presetModalState, open: true, warehouseObj: warehouseObj });
    };

    // Table Settings
    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["colli_types"] || "Colli types"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(id => id.ColliTypeID))
        }
    ]

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`ColliType`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    }

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                localization={localization(translations)}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}

                onChangeColumnHidden={columnHidden}
                actions={tableActions}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {hasPermission(permissions, 'colli_type_delete') ? (
                <ColliTypeDelete
                    activeCustomerID={activeCustomerID}
                    entityName={translations['colli_types'] || 'Colli types'}
                    translations={translations}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    deleteModalState={deleteModalState}
                    deleting={deleting}
                />
            ) : null}
            {presetModalState.open ? <ColliTypePresetForm
                presetFormState={presetModalState}
                setPresetFormState={setPresetModalState}
                handleClosePresetSafe={handleClosePresetSafe}
                handleClosePresetDanger={handleClosePresetDanger}
                onGeneratePreset={onGeneratePreset}
                translations={translations}
                permissions={permissions}
            />
                : null}
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
        colli: state.logistic.colliType,
        fetching: state.logistic.colliType.fetching,
        deleting: state.logistic.colliType.deleting
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchColliTypeListCount: filter => dispatch(actions.fetchColliTypeCount(filter)),
        onFetchColliTypeList: filter => dispatch(actions.fetchColliType(filter)),
        onBulkDeleteColliTypeTypes: data => dispatch(actions.bulkDeleteColliTypes(data)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        onGeneratePreset: data => dispatch(actions.generatePreset(data))
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(ColliTypeList));


/**
 *  Translations:
 *      colli_type
 *      colli_types
 *      edit_colli_type
 */